// Copyright (c) 2022 by Move Closer

import { DirectiveOptions, VueConstructor } from 'vue'
import { AnyObject, Container } from '@movecloser/front-core'

import { Closable } from './directives/closable'
import { collapse } from './directives/collapse'
import { filterCurrencyFactory, filterDate } from './filters'
import { IStoreConfigService, StoreConfigServiceType } from '../front/shared/services/store-config'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export interface ExtensionsRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  directives: Record<string, DirectiveOptions>
  filters: Record<string, CallableFunction>
}

/**
 * https://github.com/vuejs/vue/issues/4982#issuecomment-896650928
 */
function lazyDirective (directivePromiseFactory: () => Promise<DirectiveOptions>): DirectiveOptions {
  const DIRECTIVE_HOOKS: (keyof DirectiveOptions)[] = ['bind', 'inserted', 'update', 'componentUpdated', 'unbind']

  let promise: Promise<DirectiveOptions>

  return DIRECTIVE_HOOKS.reduce((acc, hook) => {
    acc[hook] = async (...args) => {
      if (!promise) promise = directivePromiseFactory()

      const directive = (await promise)

      // if (directive.default) directive = directive.default
      if (!directive[hook]) return

      return directive[hook]?.apply(null, args)
    }

    return acc
  }, {} as DirectiveOptions)
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export async function registerExtensions (Vue: VueConstructor, container?: Container, configuration?: AnyObject): Promise<void> {
  let currencyCode: string | undefined

  if (container) {
    const storeConfigService = container.get<IStoreConfigService>(StoreConfigServiceType)

    const storeConfig = await storeConfigService.getConfig()

    currencyCode = isValidConfig(storeConfig) ? storeConfig.defaultDisplayCurrencyCode : undefined
  }

  const extensions: ExtensionsRegistry = {
    filters: {
      currency: filterCurrencyFactory(currencyCode, configuration?.currency),
      dateFormatter: filterDate
    },
    directives: {
      closable: Closable,
      collapse: collapse,
      clamp: lazyDirective(() => import(/* webpackChunkName: "directive/clamp" */ './directives/clamp').then(({ clamp }) => clamp)),
      closePopover: lazyDirective(() => import(/* webpackChunkName: "directive/tooltip" */ 'v-tooltip').then(({ VClosePopover }) => VClosePopover)),
      tooltip: lazyDirective(() => import(/* webpackChunkName: "directive/tooltip" */ 'v-tooltip').then(({ VTooltip }) => VTooltip))
    }
  }

  // Register all custom directives
  for (const [name, handler] of Object.entries(extensions.directives)) {
    Vue.directive(name, handler)
  }
  // Register all custom filters
  for (const [name, filter] of Object.entries(extensions.filters)) {
    Vue.filter(name, filter)
  }
}

/**
 * TODO: Make more checks if necessary (ex `value` in requiredConfigMap)
 */
function isValidConfig (config: unknown): config is AnyObject {
  if (typeof config === 'object' && config !== null && Object.keys(config).length > 0) {
    return true
  }

  return false
}
